import type { ID, ISODateTime } from './ids';

/**
 * **A CLUB'S APPLICATION** (O18, clubs.kiddiekredit.com/apply; the admin's Applications page; Evan, 2026-09-27). Anyone
 * can apply with no account; every Kiddie Kredit owner is emailed; an owner approves (the club is made and its
 * director emailed the set-up link) or declines (a kind email, and they can apply again). Declined applications are
 * deleted after 12 months. An application from a village's "Invite your club" link carries that village's code, and
 * when it is approved that village's grown-ups are told.
 */

/** "Type of organization" on O18. */
export const CLUB_ORG_TYPES = ['school', 'club', 'library', 'faith', 'other'] as const;
export type ClubOrgType = (typeof CLUB_ORG_TYPES)[number];

/** Declined applications are deleted this long after the decision. */
export const DECLINED_APPLICATION_MONTHS = 12;
/** Applications from one network address an hour before it waits. */
export const CLUB_APPLICATION_HOURLY_LIMIT = 5;
/** Every text field's limit, trimmed. */
export const CLUB_APPLICATION_FIELD_MAX = 200;

/** O18's fields, as drawn. */
export interface ClubApplicationInput {
  clubName: string;
  contactName: string;
  email: string;
  phone: string;
  /** "City and state" — one field. */
  cityState: string;
  /** "About how many kids". */
  kidsCount: string;
  /** "Ages of the kids". */
  kidsAges: string;
  orgType: ClubOrgType;
  /** "How did you hear about us?" */
  heardFrom: string;
  website?: string;
  /** The `?ref=` village code from a family's "Invite your club" link. */
  ref?: string;
  /**
   * **The hidden field** a person never sees and a bot fills in. Anything in it and the application is thanked and
   * dropped: nothing is stored or sent, and the answer is the same as a real one's.
   */
  homepage?: string;
}

export type ClubApplicationStatus = 'new' | 'approved' | 'declined';

/** A row on the admin's Applications page (owners only). */
export interface ClubApplication extends Omit<ClubApplicationInput, 'homepage'> {
  id: ID;
  status: ClubApplicationStatus;
  submittedAt: ISODateTime;
  /** When `ref` named a village: its name, so the owner sees who sent them. */
  referredBy?: { villageName: string };
  decidedAt?: ISODateTime;
  /** Approved: the club it became. */
  orgId?: ID;
}

/** Approve: the club takes the application's name unless the owner shortens it for dense rows. */
export interface ApproveClubApplicationInput {
  id: ID;
  shortName?: string;
  initials?: string;
}

/** The inbox and push kind that tells a village its club arrived: "{club} is on Kiddie Kredit. They can invite your village now." */
export const CLUB_JOINED_TYPE = 'club_joined';
